import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  createTheme,
  MantineColorsTuple,
  MantineProvider,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import "./i18n";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { BrowserRouter as Router } from "react-router-dom";
import { CartProvider } from "./components/Cart/CartContext";
import { SessionProvider } from "./auth/SessionContext";
import { ConsentProvider } from "./components/CookieBanner/ConsentContext";
import CheckRefLink from "./CheckReflink";
import CookieModal from "./components/CookieBanner/CookieModal";
import { isPrerender } from "./seo/lang";

export const DEBUG = process.env.NODE_ENV === 'development';

const accDriveColors: MantineColorsTuple = [
  "#effcea",
  "#e3f2da",
  "#c6e5b6",
  "#a8d68f",
  "#8dc96d",
  "#7dc257",
  "#74be4c",
  "#62a73c",
  "#559434",
  "#468027",
];

const theme = createTheme({
  colors: {
    accDriveColors,
  },
});
const rootElement = document.getElementById("root") as HTMLElement;

const app = (
  <React.StrictMode>
    <MantineProvider defaultColorScheme="dark" theme={theme}>
      <ConsentProvider>
        <CheckRefLink />
        {/* Left out of the prerender so crawlers read the hero first, not the cookie notice. */}
        {!isPrerender() && <CookieModal />}
        <ModalsProvider>
          <Suspense fallback={<></>}>
            <Notifications />
            <Router>
              <SessionProvider>
                <CartProvider>
                  <App />
                </CartProvider>
              </SessionProvider>
            </Router>
          </Suspense>
        </ModalsProvider>
      </ConsentProvider>
    </MantineProvider>
  </React.StrictMode>
);

// Deliberately NOT hydrateRoot, even though react-snap leaves prerendered markup in
// #root. Hydration requires the first client render to match that markup, and it cannot
// here: the translations load over HTTP (i18n.js, i18next-http-backend) while the whole
// app sits behind <Suspense>, so the browser's first render is the empty fallback while
// the prerendered HTML is fully translated — React bails out with error #418 and the
// page goes dead. Language detection makes it worse: the prerender has no localStorage
// and falls back to 'gb' while the visitor may be on 'de'.
//
// createRoot discards the prerendered DOM and renders from scratch. Crawlers still get
// the prerendered HTML, visitors get a working app. Real hydration would mean bundling
// the translations into the JS and prerendering per language.
ReactDOM.createRoot(rootElement).render(app);

reportWebVitals();
